import { useParams, Navigate, useNavigate, Link } from 'react-router-dom';
import { MarketingLayout } from '@/components/MarketingLayout';
import { Seo } from '@/components/Seo';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { getGuide } from '@/data/guides';
import { ArrowRight, ArrowLeft, CheckCircle2, Wrench } from 'lucide-react';

export default function GuideArticlePage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const guide = getGuide(slug);

  if (!guide) return <Navigate to="/guides" replace />;

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: guide.title,
    description: guide.description,
    datePublished: guide.datePublished,
    dateModified: guide.datePublished,
    author: { '@type': 'Organization', name: 'VendorSuite' },
    publisher: {
      '@type': 'Organization',
      name: 'VendorSuite',
      logo: { '@type': 'ImageObject', url: 'https://vendorsuite.io/og-image.png' },
    },
    mainEntityOfPage: `https://vendorsuite.io/guides/${guide.slug}`,
  };

  return (
    <MarketingLayout>
      <Seo
        title={guide.title}
        description={guide.description}
        path={`/guides/${guide.slug}`}
        type="article"
        jsonLd={jsonLd}
      />
      <article data-testid="guide-article-page" className="max-w-3xl mx-auto px-6 py-14">
        <Link to="/guides" className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center mb-6">
          <ArrowLeft className="mr-1 h-3 w-3" /> All guides
        </Link>

        <Badge variant="secondary" className="mb-4">{guide.tag}</Badge>
        <h1 className="text-4xl font-bold mb-4 leading-tight" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
          {guide.title}
        </h1>
        <p className="text-lg text-muted-foreground mb-10">{guide.excerpt}</p>

        <div className="space-y-10">
          {guide.sections.map((s, i) => (
            <section key={i}>
              <h2 className="text-2xl font-semibold mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                {s.heading}
              </h2>
              {(s.paragraphs || []).map((p, j) => (
                <p key={j} className="text-muted-foreground leading-relaxed mb-4">{p}</p>
              ))}
              {s.list && (
                <ul className="space-y-2">
                  {s.list.map((item, k) => (
                    <li key={k} className="flex items-start gap-2 text-muted-foreground">
                      <CheckCircle2 className="h-4 w-4 text-primary mt-1 shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          ))}
        </div>

        <div className="mt-12 rounded-2xl border bg-muted/20 p-6 flex flex-col sm:flex-row sm:items-center gap-4" data-testid="guide-related-tool">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary shrink-0">
            <Wrench className="h-5 w-5" />
          </div>
          <div className="flex-1">
            <p className="font-semibold text-sm" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Free tool: COI Requirement Builder</p>
            <p className="text-sm text-muted-foreground">Generate a recommended insurance requirement checklist by property and vendor type in seconds.</p>
          </div>
          <Button variant="outline" size="sm" onClick={() => navigate('/tools/coi-requirement-builder')} data-testid="guide-tool-link">
            Open the builder <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>

        <div className="mt-6 rounded-2xl border bg-muted/30 p-8 text-center">
          <h2 className="text-xl font-bold mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            Automate this with VendorSuite
          </h2>
          <p className="text-muted-foreground text-sm mb-5">
            AI extraction, per-property requirement rules, and automated chasing—free for 30 days, no credit card.
          </p>
          <Button onClick={() => navigate('/signup')} data-testid="guide-cta-button">
            Start Free 30-Day Trial <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
      </article>
    </MarketingLayout>
  );
}
